// Import the location generator module
// Assuming locationGenerator.js is in the same directory
// import './locationGenerator.js';

import { iconFont } from './icon-fonts.js';

const USE_STATIC_LOCATION = false;
const STATIC_LOCATION = { latitude: 43.6699136, longitude: -79.4263552, nearest_station: "Ossington", line: "line2" };

// Global variables
let lastKnownLocation = null;
let watchPositionId = null;


// Global variables for live tracking generator
let liveTrackingGenerator = null;
let isLiveTrackingActive = false;
let liveTrackingInterval = null;

/**
 * Generator function for live location tracking
 * Yields location updates every 60 seconds
 */
async function* liveLocationTracker() {
    console.log('🔄 Live location tracking generator started');

    while (true) {
        try {
            // Don't require user gesture for ongoing tracking after initial permission
            const locationResult = await getCurrentLocation(false);

            if (locationResult.success) {
                console.log('📍 Generator: Got fresh location data');

                // Check if location has changed significantly
                if (hasLocationChanged(lastKnownLocation, locationResult.location)) {
                    console.log('📍 Generator: Location changed! Updating...');

                    // Update location data with nearest station info
                    const updatedLocation = await updateLocationData(locationResult.location);

                    // Update global state
                    lastKnownLocation = { ...updatedLocation };

                    // Save to localStorage
                    localStorage.setItem('userLocation', JSON.stringify(updatedLocation));

                    // Yield the updated location data
                    yield {
                        success: true,
                        location: updatedLocation,
                        changed: true,
                        timestamp: Date.now()
                    };
                } else {
                    console.log('📍 Generator: Location unchanged');

                    // Yield indication that location was checked but unchanged
                    yield {
                        success: true,
                        location: lastKnownLocation,
                        changed: false,
                        timestamp: Date.now()
                    };
                }
            } else {
                console.error('❌ Generator: Failed to get location:', locationResult.error);

                // Yield error but don't stop the generator
                yield {
                    success: false,
                    error: locationResult.error,
                    location: lastKnownLocation,
                    timestamp: Date.now()
                };
            }
        } catch (err) {
            console.error('❌ Generator: Error in live tracking:', err);

            yield {
                success: false,
                error: err,
                location: lastKnownLocation,
                timestamp: Date.now()
            };
        }

        // Add delay to prevent too frequent calls
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
}

/**
 * Start live location tracking with generator
 * @param {number} intervalMs - Interval in milliseconds (default: 60000 = 60 seconds)
 * @param {Function} onLocationUpdate - Optional callback for location updates
 * @returns {Promise<Object>} - Success/failure result
 */
async function startLiveLocationGenerator(intervalMs = 60000, onLocationUpdate = null) {
    if (isLiveTrackingActive) {
        console.log('📍 Live tracking already active');
        return { success: true, message: 'Already active' };
    }

    try {
        console.log(`🚀 Starting live location generator (${intervalMs / 1000}s intervals)`);

        if (!navigator.geolocation) {
            throw new Error('Geolocation is not supported by your browser.');
        }

        // Wait for map data to be available
        await waitForMyData();

        // Get initial location - this MUST be called from user gesture
        const initialLocation = await getCurrentLocation(true); // Require user gesture
        if (initialLocation.success) {
            const updatedLocation = await updateLocationData(initialLocation.location);
            lastKnownLocation = { ...updatedLocation };

            // Save location preference and data
            setLocationEnabled(true);
            setLocationUsedBefore();
            localStorage.setItem('userLocation', JSON.stringify(updatedLocation));
            hideLocationButton();

            console.log('✅ Initial location set:', updatedLocation);
        } else {
            throw new Error(initialLocation.error?.message || 'Failed to get initial location');
        }

        // Create and start the generator
        liveTrackingGenerator = liveLocationTracker();
        isLiveTrackingActive = true;

        // Set up interval to call generator every specified interval
        liveTrackingInterval = setInterval(async () => {
            if (isLiveTrackingActive && liveTrackingGenerator) {
                try {
                    const result = await liveTrackingGenerator.next();

                    if (!result.done && result.value) {
                        console.log('📍 Generator yielded:', result.value);

                        // Call optional callback with the result
                        if (onLocationUpdate && typeof onLocationUpdate === 'function') {
                            onLocationUpdate(result.value);
                        }

                        // Trigger custom event for other parts of the app
                        const event = new CustomEvent('liveLocationUpdate', {
                            detail: result.value
                        });
                        window.dispatchEvent(event);
                    }
                } catch (err) {
                    console.error('❌ Error processing generator result:', err);
                }
            }
        }, intervalMs);

        console.log('✅ Live location generator started successfully');
        return {
            success: true,
            location: lastKnownLocation,
            message: `Live tracking active with ${intervalMs / 1000}s intervals`
        };

    } catch (err) {
        console.error('❌ Error starting live location generator:', err);
        isLiveTrackingActive = false;
        return { success: false, error: err };
    }
}

/**
 * Stop live location tracking generator
 */
function stopLiveLocationGenerator() {
    console.log('🛑 Stopping live location generator...');

    isLiveTrackingActive = false;

    // Clear the interval
    if (liveTrackingInterval) {
        clearInterval(liveTrackingInterval);
        liveTrackingInterval = null;
    }

    // Clean up generator
    if (liveTrackingGenerator) {
        try {
            liveTrackingGenerator.return(); // Properly close the generator
        } catch (err) {
            console.error('Error closing generator:', err);
        }
        liveTrackingGenerator = null;
    }

    console.log('✅ Live location generator stopped');
}

/**
 * Check if live tracking is currently active
 * @returns {boolean}
 */
function isLiveTrackingEnabled() {
    return isLiveTrackingActive;
}

/**
 * Get the current status of live tracking
 * @returns {Object} - Status information
 */
function getLiveTrackingStatus() {
    return {
        active: isLiveTrackingActive,
        hasGenerator: liveTrackingGenerator !== null,
        hasInterval: liveTrackingInterval !== null,
        lastKnownLocation: lastKnownLocation
    };
}

// // Make new functions available globally
window.startLiveLocationGenerator = startLiveLocationGenerator;
window.stopLiveLocationGenerator = stopLiveLocationGenerator;
window.isLiveTrackingEnabled = isLiveTrackingEnabled;
window.getLiveTrackingStatus = getLiveTrackingStatus;


///////////////////END OF GENERATOR CODE


/**
 * Get current location (static or GPS)
 * @param {boolean} requireUserGesture - Whether this call requires a user gesture (for initial permission)
 */
async function getCurrentLocation(requireUserGesture = false) {
    if (USE_STATIC_LOCATION) {
        console.log('Using static location for development');
        localStorage.setItem('userLocation', JSON.stringify(STATIC_LOCATION));
        return {
            success: true,
            location: STATIC_LOCATION,
            error: null
        };
    }

    if (!navigator.geolocation) {
        return {
            success: false,
            location: null,
            error: new Error('Geolocation is not supported by your browser.')
        };
    }

    // For ongoing tracking (not requiring user gesture), check if we have permission
    if (!requireUserGesture) {
        const status = await getLocationEnabledStatus();
        if (!status.fullyEnabled) {
            return {
                success: false,
                location: null,
                error: new Error('Geolocation permission not granted.')
            };
        }
    }

    // For initial requests (requiring user gesture), we'll directly call geolocation to trigger permission
    console.log(`📍 Requesting location (requireUserGesture: ${requireUserGesture})`);

    try {
        const coords = await new Promise((resolve, reject) => {
            // Set appropriate options based on whether this is initial request or ongoing tracking
            const options = requireUserGesture ? {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0  // Force fresh location for initial request
            } : {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 30000
            };

            console.log(`📍 Calling navigator.geolocation.getCurrentPosition with options:`, options);

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const result = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    };
                    localStorage.setItem('userLocation', JSON.stringify(result));
                    console.log('📍 Location obtained successfully:', result);
                    resolve(result);
                },
                (error) => {
                    console.error('📍 Geolocation error:', error.code, error.message);

                    // Provide more specific error messages
                    let errorMessage = error.message;
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = "Location access denied by user";
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = "Location information unavailable";
                            break;
                        case error.TIMEOUT:
                            errorMessage = "Location request timed out";
                            break;
                    }

                    const enhancedError = new Error(errorMessage);
                    enhancedError.code = error.code;
                    reject(enhancedError);
                },
                options
            );
        });

        return {
            success: true,
            location: coords,
            error: null
        };
    } catch (err) {
        console.error('📍 Failed to get location:', err);
        return {
            success: false,
            location: null,
            error: err
        };
    }
}

/**
 * Haversine formula for distance calculation
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // metres
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) *
        Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
}

/**
 * Find nearest station to user location
 */
async function findNearestStation() {
    try {
        let nearest = null;
        let minDistance = -1;
        const userLocation = loadSavedLocation();

        const dataMap = {
            'line1University': window.line1University,
            'line1Yonge': window.line1Yonge,
            'line2': window.line2,
            'line4': window.line4,
            'line5': window.line5,
            'line6': window.line6
        };

        for (const [key, branch] of Object.entries(dataMap)) {
            if (!branch || !branch.stations) continue;

            for (const station of branch.stations) {
                const dist = calculateDistance(
                    userLocation.latitude,
                    userLocation.longitude,
                    station.lat,
                    station.lon
                );

                if (dist < minDistance || minDistance === -1) {
                    minDistance = dist;
                    nearest = station;
                    nearest.line = key;
                }
            }
        }

        if (nearest) {
            console.log(`Nearest station is ${nearest.name}, ${minDistance.toFixed(0)} m away.`);
        }

        return nearest;
    } catch (err) {
        console.error('Error finding nearest station:', err);
        return null;
    }
}

/**
 * Load saved location from localStorage
 */
function loadSavedLocation() {
    const stored = localStorage.getItem('userLocation');
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch (e) {
            console.error('Failed to parse stored location:', e);
            return null;
        }
    }
    return null;
}

/**
 * Wait for map data to be available
 */
function waitForMyData(maxWaitMs = 10000, intervalMs = 100) {
    return new Promise((resolve, reject) => {
        const start = Date.now();

        const check = () => {
            if (window.line1University && window.line1Yonge && window.line2 && window.line4 && window.line5 && window.line6) {
                const dataMap = {
                    'line1University': window.line1University,
                    'line1Yonge': window.line1Yonge,
                    'line2': window.line2,
                    'line4': window.line4,
                    'line5': window.line5,
                    'line6': window.line6
                };
                resolve(dataMap);
            } else if (Date.now() - start > maxWaitMs) {
                reject(new Error('Timed out waiting for line data'));
            } else {
                setTimeout(check, intervalMs);
            }
        };

        check();
    });
}

/**
 * Check if two locations are significantly different
 */
function hasLocationChanged(location1, location2, threshold = 50) {
    if (!location1 || !location2) return true;
    if (!location1.latitude || !location1.longitude || !location2.latitude || !location2.longitude) return true;

    const distance = calculateDistance(
        location1.latitude,
        location1.longitude,
        location2.latitude,
        location2.longitude
    );

    return distance > threshold;
}

/**
 * Update location data with nearest station info
 */
async function updateLocationData(locationData) {
    if (!window.line1University || !window.line1Yonge || !window.line2 || !window.line4 || !window.line5 || !window.line6) {
        console.log('Map data not available for location update');
        return locationData;
    }

    try {
        localStorage.setItem('userLocation', JSON.stringify(locationData));

        const station = await findNearestStation();
        if (station) {
            locationData.nearest_station = station.name;
            locationData.uuid = station.uuid;
            locationData.line = station.line;

            const distance = calculateDistance(
                locationData.latitude,
                locationData.longitude,
                station.lat,
                station.lon
            );
            locationData.distance_to_nearest = distance;

            // Add visual marker to map
            addUserLocationToMap(locationData);

            // Trigger location update event for UI updates
            const event = new CustomEvent('locationDataUpdated', {
                detail: { location: locationData, station, distance }
            });
            window.dispatchEvent(event);
        }

        return locationData;
    } catch (err) {
        console.error('Error updating location data:', err);
        return locationData;
    }
}


/**
 * Check browser geolocation permission status
 * @returns {Promise<string>} - 'granted', 'denied', 'prompt', or 'unsupported'
 */
async function getGeolocationPermission() {
    if (!navigator.permissions) {
        console.log('Permissions API not supported');
        return 'unsupported';
    }

    try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        return permission.state; // 'granted', 'denied', or 'prompt'
    } catch (err) {
        console.error('Error checking geolocation permission:', err);
        return 'unsupported';
    }
}

/**
 * Enhanced location enabled check - combines user preference AND browser permission
 * @returns {Promise<Object>} - Detailed status object
 */
async function getLocationEnabledStatus() {
    const userEnabled = localStorage.getItem('locationEnabled') === 'true';
    const browserPermission = await getGeolocationPermission();
    const hasGeolocation = !!navigator.geolocation;

    return {
        userEnabled,           // User's preference in localStorage
        browserPermission,     // Browser permission: 'granted', 'denied', 'prompt', 'unsupported'
        hasGeolocation,        // Browser supports geolocation API
        fullyEnabled: userEnabled && (browserPermission === 'granted'),
        canRequest: hasGeolocation && (browserPermission === 'prompt' || browserPermission === 'granted'),
        isBlocked: browserPermission === 'denied'
    };
}

/**
 * Simple check if location is fully enabled (user + browser)
 * @returns {Promise<boolean>}
 */
async function isLocationEnabled() {
    const status = await getLocationEnabledStatus();
    return status.fullyEnabled;
}

/**
 * Check if we can request location permission
 * @returns {Promise<boolean>}
 */
async function canRequestLocationPermission() {
    const status = await getLocationEnabledStatus();
    return status.canRequest;
}

/**
 * Check if location is blocked by browser
 * @returns {Promise<boolean>}
 */
async function isLocationBlocked() {
    const status = await getLocationEnabledStatus();
    return status.isBlocked;
}

/**
 * Listen for permission changes (if supported)
 */
function listenForPermissionChanges() {
    if (!navigator.permissions) return;

    navigator.permissions.query({ name: 'geolocation' })
        .then(permission => {
            permission.addEventListener('change', () => {
                console.log('📍 Geolocation permission changed to:', permission.state);

                // Dispatch custom event
                const event = new CustomEvent('geolocationPermissionChanged', {
                    detail: {
                        state: permission.state,
                        timestamp: Date.now()
                    }
                });
                window.dispatchEvent(event);
            });
        })
        .catch(err => {
            console.error('Error setting up permission listener:', err);
        });
}

function setLocationEnabled(enabled) {
    localStorage.setItem('locationEnabled', enabled.toString());
    console.log(`📍 Location tracking ${enabled ? 'enabled' : 'disabled'} by user`);
}

function hasUsedLocationBefore() {
    return localStorage.getItem('locationUsedBefore') === 'true';
}

function setLocationUsedBefore() {
    localStorage.setItem('locationUsedBefore', 'true');
    console.log('📍 Marked location as used before');
}


/**
 * Enhanced initialization that checks both user preference and browser permission
 */
async function initializeLocationServices() {
    console.log('📍 Initializing location services with permission checks...');

    const status = await getLocationEnabledStatus();
    console.log('📍 Location status:', status);

    if (!status.hasGeolocation) {
        console.log('❌ Geolocation not supported by browser');
        window.dispatchEvent(new CustomEvent('geolocationNotSupported'));
        return;
    }

    if (status.isBlocked) {
        console.log('❌ Geolocation blocked by browser');
        window.dispatchEvent(new CustomEvent('geolocationBlocked'));
        return;
    }

    // DON'T auto-start location tracking - wait for user gesture
    if (status.fullyEnabled) {
        console.log('✅ Location was previously enabled - showing option to re-enable');
        const savedLocation = loadSavedLocation();
        if (savedLocation) {
            window.dispatchEvent(new CustomEvent('savedLocationAvailable', {
                detail: { location: savedLocation }
            }));
        }
    } else if (status.userEnabled && status.browserPermission === 'prompt') {
        console.log('📍 User enabled but permission not granted - showing prompt option');
        window.dispatchEvent(new CustomEvent('locationPermissionNeeded'));
    } else if (status.browserPermission === 'granted' && !status.userEnabled) {
        console.log('📍 Browser permission granted but user hasn\'t enabled - showing saved location');
        const savedLocation = loadSavedLocation();
        if (savedLocation) {
            window.dispatchEvent(new CustomEvent('savedLocationAvailable', {
                detail: { location: savedLocation }
            }));
        }
    } else {
        console.log('📍 Location not enabled - showing enable option');
        window.dispatchEvent(new CustomEvent('locationDisabled'));
    }

    // Set up permission change listener
    listenForPermissionChanges();
}

/**
 * Enhanced enable function with permission checking
 */
async function enableLocationTracking() {
    try {
        console.log('📍 User enabling location tracking...');

        const status = await getLocationEnabledStatus();

        if (status.isBlocked) {
            throw new Error('Geolocation is blocked. Please enable it in your browser settings.');
        }

        if (!status.hasGeolocation) {
            throw new Error('Geolocation is not supported by your browser.');
        }

        // Try to start live tracking (this will trigger permission request if needed)
        const result = await window.startLiveLocationGenerator(60000, (locationUpdate) => {
            console.log('📍 Live location update:', locationUpdate);

            const event = new CustomEvent('userLocationUpdate', {
                detail: locationUpdate
            });
            window.dispatchEvent(event);
        });

        if (result.success) {
            setLocationEnabled(true);
            setLocationUsedBefore();
            console.log('✅ Location tracking enabled successfully');

            const event = new CustomEvent('locationTrackingEnabled', {
                detail: result
            });
            window.dispatchEvent(event);

            return result;
        } else {
            throw new Error(result.error?.message || 'Failed to enable location tracking');
        }
    } catch (err) {
        console.error('❌ Error enabling location tracking:', err);

        // Check if it's a permission error
        const status = await getLocationEnabledStatus();
        if (status.isBlocked) {
            const event = new CustomEvent('locationPermissionDenied', {
                detail: { error: err }
            });
            window.dispatchEvent(event);
        } else {
            const event = new CustomEvent('locationTrackingError', {
                detail: { error: err }
            });
            window.dispatchEvent(event);
        }

        return { success: false, error: err };
    }
}

/**
 * Disable location tracking
 */
function disableLocationTracking() {
    console.log('📍 User disabling location tracking...');

    // Stop the live generator
    if (window.stopLiveLocationGenerator) {
        window.stopLiveLocationGenerator();
    }


    // Stop watchPosition-based tracking
    if (watchPositionId !== null) {
        navigator.geolocation.clearWatch(watchPositionId);
        watchPositionId = null;
        console.log('📍 Stopped watching position');
    }

    // Stop the old monitoring interval (if function exists)
    try {
        if (typeof window.stopLocationMonitoring === 'function') {
            window.stopLocationMonitoring();
        }
    } catch (e) {
        console.log('📍 stopLocationMonitoring not available:', e.message);
    }

    // Remove location circles from map (if function exists)
    try {
        if (typeof window.removeAllLocationMarkers === 'function') {
            window.removeAllLocationMarkers();
        }
    } catch (e) {
        console.log('📍 removeAllLocationMarkers not available:', e.message);
    }

    // Clear location data
    lastKnownLocation = null;
    localStorage.removeItem('userLocation');
    setLocationEnabled(false);

    // Trigger disabled event
    const event = new CustomEvent('locationTrackingDisabled');
    window.dispatchEvent(event);

    console.log('✅ Location tracking disabled');
}

/**
 * Get current location status
 */
async function getLocationStatus() {
    return {
        enabled: await isLocationEnabled(),
        hasUsedBefore: hasUsedLocationBefore(),
        lastKnownLocation: lastKnownLocation,
        savedLocation: loadSavedLocation(),
        liveTrackingActive: window.isLiveTrackingEnabled ? window.isLiveTrackingEnabled() : false
    };
}

/**
 * Hide the location button
 */
function hideLocationButton() {
    const button = document.getElementById('showLocationBtn');
    if (button) {
        button.style.display = 'none';
        console.log('📍 Location button hidden');
    }
}

/**
 * Remove all location markers from the map
 */
function removeAllLocationMarkers() {
    // Remove user location circles
    document.querySelectorAll('.user-location-circle').forEach(circle => circle.remove());
    // Remove proximity indicators
    document.querySelectorAll('.proximity-indicator').forEach(indicator => indicator.remove());
    console.log('📍 All location markers removed');
}

/**
 * Add user location circle to all SVG maps
 * @param {Object} userLocation - User location object with latitude and longitude
 */
function addUserLocationToMap(userLocation) {
    if (!userLocation || !userLocation.latitude || !userLocation.longitude) {
        console.log('No valid user location to display');
        return;
    }

    console.log('Adding user location to map:', userLocation);

    // Wait a bit for the map to be fully rendered
    setTimeout(() => {
        // Remove any existing user location circles
        removeAllLocationMarkers();

        // Add to all available SVG maps to ensure visibility
        addUserLocationToResponsiveMaps(userLocation);
    }, 500);
}

/**
 * Add user location to responsive map SVGs
 * @param {Object} userLocation - User location object
 */
function addUserLocationToResponsiveMaps(userLocation) {
    console.log('🎯 Adding user location to maps:', userLocation);

    // First, find the nearest station across all lines to determine which SVG to use
    let nearestStation = null;
    let nearestLine = null;
    let minDistance = Infinity;

    const lineData = {
        'line1University': window.line1University,
        'line1Yonge': window.line1Yonge,
        'line2': window.line2,
        'line4': window.line4,
        'line5': window.line5,
        'line6': window.line6
    };

    // Find the actual nearest station across all lines
    for (const [lineKey, data] of Object.entries(lineData)) {
        if (!data || !data.stations) continue;

        data.stations.forEach(station => {
            const distance = calculateDistance(
                userLocation.latitude,
                userLocation.longitude,
                station.lat,
                station.lon
            );

            if (distance < minDistance) {
                minDistance = distance;
                nearestStation = station;
                nearestLine = lineKey;
            }
        });
    }

    if (!nearestStation || !nearestLine) {
        console.log('❌ No nearest station found');
        return;
    }

    console.log(`🎯 Nearest station: ${nearestStation.name} on ${nearestLine} (${minDistance.toFixed(0)}m away)`);

    // For debugging: Always show the location dot regardless of distance
    // TODO: Restore distance-based logic later if needed
    if (minDistance > 1000) { // Increased threshold from 100m to 500m for better user experience
        console.log(`📍 User is ${minDistance.toFixed(0)}m from nearest station - showing distance banner`);
        // Show distance banner if function exists
        if (typeof window.showLocationBanner === 'function') {
            window.showLocationBanner();
        }
        removeAllLocationMarkers();
        return;
    }

    console.log(`📍 User is ${minDistance.toFixed(0)}m from nearest station - showing location dot`);

    // Map line keys to SVG IDs
    const svgIdMap = {
        'line1University': 'line1-university-svg',
        'line1Yonge': 'line1-yonge-svg',
        'line2': 'line2-svg',
        'line4': 'line4-svg'
    };

    const svgId = svgIdMap[nearestLine];
    const svg = document.getElementById(svgId);

    if (!svg) {
        console.log(`❌ SVG not found: ${svgId}`);
        return;
    }

    console.log(`✅ Found SVG: ${svgId}`);

    // Get the line data for coordinate calculation
    const data = lineData[nearestLine];

    // Find the station index in the line data
    let stationIndex = -1;
    data.stations.forEach((station, index) => {
        if (station.name === nearestStation.name) {
            stationIndex = index;
        }
    });

    if (stationIndex === -1) {
        console.log(`❌ Station ${nearestStation.name} not found in ${nearestLine} data`);
        return;
    }

    console.log(`🎯 Station index: ${stationIndex}`);

    // Calculate SVG coordinates based on station position
    const lineWidth = 200;
    let stationSpacing = 32.5;

    // Adjust spacing for different lines
    if (svgId === 'line2-svg') {
        stationSpacing = 32.5 * 0.8 * 1.15 * 1.15;
    } else if (svgId === 'line4-svg') {
        stationSpacing = 32.5 * 1.5;
    }

    const startY = 70;
    const fixedX = (lineWidth / 2) * 0.5 - 20;

    // Calculate Y position based on station index
    const stationY = startY + stationIndex * stationSpacing;

    // Add some offset to show the location dot near but not exactly on the station
    const offsetX = 15; // Offset to the right of the line
    const locationX = fixedX + offsetX;
    const locationY = stationY;

    console.log(`🎯 Adding location dot at (${locationX}, ${locationY})`);

    addUserLocationCircle(svg, locationX, locationY, nearestStation);
}

/**
 * Create and add a user location circle to an SVG
 * @param {SVGElement} svg - The SVG element
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 * @param {Object} nearestStation - The nearest station object for click handling
 */
function addUserLocationCircle(svg, x, y, nearestStation = null) {
    // Create outer white circle (larger)
    const outerCircle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    outerCircle.setAttribute("cx", x);
    outerCircle.setAttribute("cy", y);
    outerCircle.setAttribute("r", "12");
    outerCircle.setAttribute("fill", "white");
    outerCircle.setAttribute("stroke", "#2563eb");
    outerCircle.setAttribute("stroke-width", "3");
    outerCircle.setAttribute("class", "user-location-circle");
    outerCircle.style.filter = "drop-shadow(0 2px 4px rgba(0,0,0,0.2))";

    // Create inner blue circle (smaller)
    const innerCircle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    innerCircle.setAttribute("cx", x);
    innerCircle.setAttribute("cy", y);
    innerCircle.setAttribute("r", "6");
    innerCircle.setAttribute("fill", "#2563eb");
    innerCircle.setAttribute("class", "user-location-circle");

    // Make circles clickable if we have a nearest station
    if (nearestStation && typeof window.handleStationClick === 'function') {
        outerCircle.style.cursor = "pointer";
        innerCircle.style.cursor = "pointer";

        const clickHandler = (e) => {
            e.stopPropagation();
            // Get the SVG ID from the parent SVG element
            const svgId = svg?.id || null;
            window.handleStationClick(e, nearestStation, svgId);
        };

        outerCircle.addEventListener("click", clickHandler);
        innerCircle.addEventListener("click", clickHandler);

        console.log(`Made user location circle clickable for station: ${nearestStation.name}`);
    }

    // Add circles to SVG
    svg.appendChild(outerCircle);
    svg.appendChild(innerCircle);

    console.log(`Added user location circle to ${svg.id} at (${x}, ${y})`);
}

/**
 * Request user location and enable location tracking
 */
async function requestUserLocation() {
    const button = document.getElementById("showLocationBtn");
    if (!button) {
        console.error('❌ Location button not found');
        return;
    }

    const originalContent = button.innerHTML;

    // Update button to show loading state
    button.disabled = true;
    button.innerHTML = `
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        <span class="font-medium">Getting Location...</span>
    `;

    try {
        // Import iconFont function
        // Enable location tracking
        const result = await enableLocationTracking();

        if (!result.success) {
            throw new Error(
                result.error?.message || "Failed to enable location tracking"
            );
        }

        // Update button to show success
        button.innerHTML = `
            ${iconFont('check_circle', 'text-lg')}
            <span class="font-medium">Location Found!</span>
        `;

        // Hide button after successful first use
        setTimeout(() => {
            hideLocationButton();
        }, 2000);

        console.log("✅ Location tracking enabled via button click");

    } catch (error) {
        console.error("❌ Error enabling location:", error);

        try {
            // Import iconFont for error display
            // Update button to show error
            button.innerHTML = `
                ${iconFont('error', 'text-lg')}
                <span class="font-medium">Location Error</span>
            `;
        } catch (importError) {
            // Fallback if iconFont import fails
            button.innerHTML = `
                <span class="font-medium">Location Error</span>
            `;
        }

        // Show error message to user
        alert(
            `Unable to get your location: ${error.message}\n\nPlease make sure location services are enabled and you've granted permission to this website.`
        );

        // Reset button after 3 seconds (only if there was an error)
        setTimeout(() => {
            button.disabled = false;
            button.innerHTML = originalContent;
        }, 3000);
    }
}

/**
 * Simple test function to directly test geolocation permission
 */
async function testGeolocationDirect() {
    console.log('🧪 Testing direct geolocation call...');

    if (!navigator.geolocation) {
        console.error('❌ Geolocation not supported');
        return;
    }

    try {
        const position = await new Promise((resolve, reject) => {
            console.log('📍 Calling navigator.geolocation.getCurrentPosition directly...');

            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    console.log('✅ Direct geolocation success:', pos.coords);
                    resolve(pos);
                },
                (error) => {
                    console.error('❌ Direct geolocation error:', error.code, error.message);
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        });

        console.log('✅ Direct test successful:', position.coords);
        return position;

    } catch (error) {
        console.error('❌ Direct test failed:', error);
        throw error;
    }
}

async function handleLocationButtonClick() {
    console.log('🔘 Location button clicked - starting permission request flow');

    // Check current permission status before attempting
    const permissionStatus = await getGeolocationPermission();
    console.log('📍 Current permission status:', permissionStatus);

    // If permission is denied, show helpful message
    if (permissionStatus === 'denied') {
        alert('Location access is blocked. Please click the location icon in your browser\'s address bar to enable location access, then try again.');
        return;
    }

    // For debugging: Try the most direct approach first
    if (permissionStatus === 'prompt') {
        console.log('🧪 Permission is prompt - testing immediate geolocation call...');

        try {
            // Call geolocation directly from button click context
            const position = await new Promise((resolve, reject) => {
                console.log('📍 Making direct geolocation call from button click...');

                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        console.log('✅ SUCCESS: Permission granted and location obtained:', pos.coords);
                        resolve(pos);
                    },
                    (error) => {
                        console.error('❌ FAILED: Geolocation error:', error.code, error.message);
                        reject(error);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 0
                    }
                );
            });

            console.log('✅ Direct call successful, proceeding with full flow...');

        } catch (error) {
            console.error('❌ Direct call failed:', error);
            alert(`Location permission test failed: ${error.message}\n\nThis might indicate a browser issue or that location services are disabled.`);
            return;
        }
    }

    // Proceed with the full location setup
    await requestUserLocation();
}

// Make functions available globally
window.getCurrentLocation = getCurrentLocation;
window.findNearestStation = findNearestStation;
window.loadSavedLocation = loadSavedLocation;
window.calculateDistance = calculateDistance;
window.hasLocationChanged = hasLocationChanged;
window.updateLocationData = updateLocationData;
window.waitForMyData = waitForMyData;
window.enableLocationTracking = enableLocationTracking;
window.disableLocationTracking = disableLocationTracking;
window.getLocationStatus = getLocationStatus;
window.isLocationEnabled = isLocationEnabled;
window.setLocationEnabled = setLocationEnabled;
window.requestUserLocation = requestUserLocation;
window.handleLocationButtonClick = handleLocationButtonClick;
window.testGeolocationDirect = testGeolocationDirect;
window.getGeolocationPermission = getGeolocationPermission;
window.getLocationEnabledStatus = getLocationEnabledStatus;
window.removeAllLocationMarkers = removeAllLocationMarkers;
window.addUserLocationToMap = addUserLocationToMap;
window.hideLocationButton = hideLocationButton;

// // Initialize when DOM is loaded
// document.addEventListener('DOMContentLoaded', () => {
//     // Small delay to ensure other scripts are loaded
//     setTimeout(initializeLocationServices, 500);
// });

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeLocationServices);
} else {
    setTimeout(initializeLocationServices, 500);
}